// Form Service - Handles form submissions via backend API
// In production, API should be on same origin, so use relative path
// In development, use explicit localhost URL
const getApiBaseUrl = () => {
  // Check if window is available (client-side)
  if (typeof window === 'undefined') {
    return '/api'; // Default for SSR
  }
  
  // Priority 1: Environment variable (most reliable for production)
  // Note: React env vars are embedded at BUILD time, not runtime
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL.trim();
    // Ensure it doesn't end with /api to avoid double /api/api
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    console.log('📡 Using REACT_APP_API_URL from environment:', baseUrl);
    return baseUrl;
  }
  
  // In development (localhost), use explicit URL
  // Try to match the port - if frontend is on 3001, use 5001; if 3000, use 5000
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const frontendPort = window.location.port;
    // Map frontend ports to backend ports
    // Always use port 5001 for backend (matches production)
    const backendUrl = 'http://localhost:5001/api';
    console.log('📡 Development mode - using explicit backend URL:', backendUrl);
    return backendUrl;
  }
  
  // In production, determine API URL based on current location
  const currentOrigin = window.location.origin;
  const isHTTPS = window.location.protocol === 'https:';
  const hostname = window.location.hostname;
  
  // Check if we're on the production domain
  const isProductionDomain = hostname === 'droshpod.com' || 
                            hostname === 'www.droshpod.com' ||
                            hostname.endsWith('.droshpod.com');
  
  if (isProductionDomain) {
    // Production: Use relative path if backend is on same origin
    // Otherwise, try to construct API URL
    // For same-origin deployments (most common), use relative path
    const apiUrl = '/api';
    console.log('📡 Production mode - using relative API path:', apiUrl);
    console.log('📡 Current origin:', currentOrigin);
    return apiUrl;
  }
  
  // Fallback: Use relative path (assumes same-origin)
  console.log('📡 Using fallback relative API path: /api');
  console.log('📡 Current origin:', currentOrigin);
  return '/api';
};

class FormService {
  // Submit contact form
  async submitContactForm(formData) {
    let apiUrl;
    try {
      const API_BASE_URL = getApiBaseUrl();
      apiUrl = `${API_BASE_URL}/contact/submit`;
      console.log('📧 FORM SERVICE: Submitting contact form via backend API');
      console.log('📧 FORM SERVICE: Form data:', formData);
      console.log('📧 FORM SERVICE: API URL:', apiUrl);
      console.log('📧 FORM SERVICE: Current origin:', window.location.origin);
      
      // Determine credentials mode based on URL
      const isAbsoluteUrl = apiUrl.startsWith('http://') || apiUrl.startsWith('https://');
      const isCrossOrigin = isAbsoluteUrl && !apiUrl.startsWith(window.location.origin);
      const credentialsMode = isCrossOrigin ? 'include' : 'same-origin';
      
      console.log('📡 Fetch configuration:', {
        apiUrl,
        currentOrigin: window.location.origin,
        isAbsoluteUrl,
        isCrossOrigin,
        credentialsMode
      });
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      }, 30000); // 30 second timeout
      
      let response;
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
          credentials: credentialsMode,
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      // Log response details for debugging
      console.log('📧 FORM SERVICE: Response status:', response.status);
      console.log('📧 FORM SERVICE: Response ok:', response.ok);
      console.log('📧 FORM SERVICE: Response content-type:', response.headers.get('content-type'));

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      
      if (!isJson) {
        // Response is not JSON - likely HTML (404 page or index.html)
        const text = await response.text();
        console.error('📧 FORM SERVICE: Received non-JSON response (likely HTML)');
        console.error('📧 FORM SERVICE: Response content-type:', contentType);
        console.error('📧 FORM SERVICE: Response preview:', text.substring(0, 200));
        console.error('📧 FORM SERVICE: Request URL:', apiUrl);
        console.error('📧 FORM SERVICE: Request origin:', window.location.origin);
        
        // Provide helpful error message
        if (text.includes('<!doctype') || text.includes('<html')) {
          throw new Error(
            'API endpoint not found. The request is returning an HTML page instead of JSON. ' +
            'This usually means:\n' +
            '1. The backend server is not running\n' +
            '2. The API route is not configured correctly\n' +
            '3. The web server is not proxying /api/* requests to the Node.js backend\n\n' +
            `Request URL: ${apiUrl}\n` +
            `Current origin: ${window.location.origin}`
          );
        }
        
        throw new Error(`Server returned non-JSON response (${contentType}). Expected application/json.`);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: `HTTP ${response.status}: ${response.statusText}` 
        }));
        console.error('📧 FORM SERVICE: Error response:', errorData);
        console.error('📧 FORM SERVICE: Response status:', response.status);
        console.error('📧 FORM SERVICE: Response statusText:', response.statusText);
        console.error('📧 FORM SERVICE: Request URL:', apiUrl);
        console.error('📧 FORM SERVICE: Request origin:', window.location.origin);
        
        // Provide more specific error messages
        if (response.status === 403 && errorData.error && errorData.error.includes('CORS')) {
          throw new Error('CORS error: The server is blocking requests from this origin. Please contact the administrator.');
        }
        
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ ==========================================');
      console.log('✅ CONTACT FORM: Submission successful!');
      console.log('✅ Response:', result);
      console.log('✅ ==========================================');
      return result;
    } catch (error) {
      console.error('📧 FORM SERVICE: Error submitting contact form:', error);
      console.error('📧 FORM SERVICE: Error name:', error.name);
      console.error('📧 FORM SERVICE: Error message:', error.message);
      
      // Handle JSON parsing errors (HTML response instead of JSON)
      if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
        const isProduction = window.location.hostname !== 'localhost' && 
                            window.location.hostname !== '127.0.0.1';
        const errorMsg = isProduction
          ? 'The server is not responding correctly. The API endpoint may not be configured properly. ' +
            'Please contact support if this problem persists. ' +
            `(Requested: ${apiUrl})`
          : `API endpoint returned HTML instead of JSON. This usually means:\n` +
            `1. The backend server is not running\n` +
            `2. The API route /api/contact/submit is not configured\n` +
            `3. The web server is not proxying /api/* requests to Node.js\n\n` +
            `Request URL: ${apiUrl}\n` +
            `Make sure the Node.js backend is running on the correct port.`;
        throw new Error(errorMsg);
      }
      
      // Provide more helpful error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const isProduction = window.location.hostname !== 'localhost' && 
                            window.location.hostname !== '127.0.0.1';
        const errorMsg = isProduction
          ? `Network error: Unable to connect to the server. ` +
            `Please check your internet connection and try again. ` +
            `If the problem persists, contact support. ` +
            `(API URL: ${apiUrl})`
          : `Network error: Unable to connect to server at ${apiUrl}. ` +
            `Please check that the API server is running and accessible. ` +
            `If this is a production deployment, ensure REACT_APP_API_URL is set correctly.`;
        console.error('📧 FORM SERVICE: Network error details:', {
          apiUrl,
          currentOrigin: window.location.origin,
          hostname: window.location.hostname,
          protocol: window.location.protocol,
          error: error.message,
          errorName: error.name,
          isProduction
        });
        throw new Error(errorMsg);
      }
      
      // Handle timeout/abort errors
      if (error.name === 'AbortError' || error.message.includes('timeout') || error.message.includes('aborted')) {
        const errorMsg = `Request timeout: The server took too long to respond. ` +
          `Please try again or contact support if the problem persists.`;
        console.error('📧 FORM SERVICE: Timeout error:', {
          apiUrl,
          currentOrigin: window.location.origin,
          error: error.message
        });
        throw new Error(errorMsg);
      }
      
      throw error;
    }
  }

  // Submit consultation form
  async submitConsultationForm(formData) {
    let apiUrl;
    try {
      const API_BASE_URL = getApiBaseUrl();
      apiUrl = `${API_BASE_URL}/contact/consultation`;
      console.log('📧 FORM SERVICE: Submitting consultation form via backend API');
      console.log('📧 FORM SERVICE: Form data:', formData);
      console.log('📧 FORM SERVICE: Message field:', formData.message);
      console.log('📧 FORM SERVICE: Message type:', typeof formData.message);
      console.log('📧 FORM SERVICE: Message length:', formData.message ? formData.message.length : 'undefined');
      console.log('📧 FORM SERVICE: API URL:', apiUrl);
      console.log('📧 FORM SERVICE: Current origin:', window.location.origin);
      
      // Determine credentials mode based on URL
      const isAbsoluteUrl = apiUrl.startsWith('http://') || apiUrl.startsWith('https://');
      const isCrossOrigin = isAbsoluteUrl && !apiUrl.startsWith(window.location.origin);
      const credentialsMode = isCrossOrigin ? 'include' : 'same-origin';
      
      console.log('📡 Fetch configuration:', {
        apiUrl,
        currentOrigin: window.location.origin,
        isAbsoluteUrl,
        isCrossOrigin,
        credentialsMode
      });
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      }, 30000); // 30 second timeout
      
      let response;
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
          credentials: credentialsMode,
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      // Log response details for debugging
      console.log('📧 FORM SERVICE: Response status:', response.status);
      console.log('📧 FORM SERVICE: Response ok:', response.ok);
      console.log('📧 FORM SERVICE: Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: `HTTP ${response.status}: ${response.statusText}` 
        }));
        console.error('📧 FORM SERVICE: Error response:', errorData);
        console.error('📧 FORM SERVICE: Response status:', response.status);
        console.error('📧 FORM SERVICE: Response statusText:', response.statusText);
        console.error('📧 FORM SERVICE: Request URL:', apiUrl);
        console.error('📧 FORM SERVICE: Request origin:', window.location.origin);
        
        // Provide more specific error messages
        if (response.status === 403 && errorData.error && errorData.error.includes('CORS')) {
          throw new Error('CORS error: The server is blocking requests from this origin. Please contact the administrator.');
        }
        
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ ==========================================');
      console.log('✅ CONSULTATION FORM: Submission successful!');
      console.log('✅ Response:', result);
      console.log('✅ ==========================================');
      return result;
    } catch (error) {
      console.error('📧 FORM SERVICE: Error submitting consultation form:', error);
      console.error('📧 FORM SERVICE: Error name:', error.name);
      console.error('📧 FORM SERVICE: Error message:', error.message);
      console.error('📧 FORM SERVICE: Error stack:', error.stack);
      
      // Provide more helpful error messages
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const isProduction = window.location.hostname !== 'localhost' && 
                            window.location.hostname !== '127.0.0.1';
        const errorMsg = isProduction
          ? `Network error: Unable to connect to the server. ` +
            `Please check your internet connection and try again. ` +
            `If the problem persists, contact support. ` +
            `(API URL: ${apiUrl})`
          : `Network error: Unable to connect to server at ${apiUrl}. ` +
            `Please check that the API server is running and accessible. ` +
            `If this is a production deployment, ensure REACT_APP_API_URL is set correctly.`;
        console.error('📧 FORM SERVICE: Network error details:', {
          apiUrl,
          currentOrigin: window.location.origin,
          hostname: window.location.hostname,
          protocol: window.location.protocol,
          error: error.message,
          errorName: error.name,
          isProduction
        });
        throw new Error(errorMsg);
      }
      
      // Handle timeout/abort errors
      if (error.name === 'AbortError' || error.message.includes('timeout') || error.message.includes('aborted')) {
        const errorMsg = `Request timeout: The server took too long to respond. ` +
          `Please try again or contact support if the problem persists.`;
        console.error('📧 FORM SERVICE: Timeout error:', {
          apiUrl,
          currentOrigin: window.location.origin,
          error: error.message
        });
        throw new Error(errorMsg);
      }
      
      throw error;
    }
  }

  // Subscribe to newsletter (placeholder - would need backend for database storage)
  async subscribeToNewsletter(email) {
    try {
      console.log('📧 FORM SERVICE: Newsletter subscription requested for:', email);
      // For now, just return success - you could implement email confirmation here
      return { success: true, message: 'Newsletter subscription request received' };
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }

  // Unsubscribe from newsletter (placeholder - would need backend for database storage)
  async unsubscribeFromNewsletter(email) {
    try {
      console.log('📧 FORM SERVICE: Newsletter unsubscription requested for:', email);
      // For now, just return success - you could implement email confirmation here
      return { success: true, message: 'Newsletter unsubscription request received' };
    } catch (error) {
      console.error('Error unsubscribing from newsletter:', error);
      throw error;
    }
  }

  // Test email functionality
  async testEmail(email = 'test@example.com') {
    try {
      const API_BASE_URL = getApiBaseUrl();
      console.log('📧 FORM SERVICE: Testing email functionality via backend API');
      const response = await fetch(`${API_BASE_URL}/contact/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      return { success: true, ...result };
    } catch (error) {
      console.error('Error sending test email:', error);
      throw error;
    }
  }
}

const formServiceInstance = new FormService();
export default formServiceInstance;
